// ════════════════════════════════════════════════════════════════════════
// LoginModal.tsx — Popup de inicio de sesión
// ════════════════════════════════════════════════════════════════════════
// CONCEPTO: Compound Component Pattern
// En lugar de un único componente con 10 props, exponemos varias piezas
// (Root, Header, Body) que comparten estado interno vía Context.
// El consumidor arma el modal como quiera:
//
//   <LoginModal.Root isOpen={open} onClose={close}>
//     <LoginModal.Header>Iniciar sesión</LoginModal.Header>
//     <LoginModal.Body onSuccess={handleSuccess} />
//   </LoginModal.Root>
//
// El contexto interno (LoginModalContext) es privado de este archivo:
// solo lo consumen los subcomponentes, nunca se exporta.
// ════════════════════════════════════════════════════════════════════════

import { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode, FormEvent } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLoginForm } from '../../hooks/useLoginForm';
import { TurnstileWidget } from '../TurnstileWidget/TurnstileWidget';
import { CloseIcon } from '../Icons/Icons';
import styles from './LoginModal.module.css';

// ── Contexto interno del compound component ───────────────────────────
interface LoginModalContextValue {
  onClose: () => void;
}
const LoginModalContext = createContext<LoginModalContextValue | undefined>(undefined);

function useLoginModalContext(): LoginModalContextValue {
  const ctx = useContext(LoginModalContext);
  if (!ctx) {
    throw new Error('Los subcomponentes de LoginModal deben usarse dentro de <LoginModal.Root>');
  }
  return ctx;
}

// ── Root ────────────────────────────────────────────────────────────────
interface RootProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
}
function Root({ isOpen, onClose, children }: RootProps) {
  if (!isOpen) return null;

  return (
    <LoginModalContext.Provider value={{ onClose }}>
      {/* Overlay: clic afuera cierra el modal */}
      <div className={styles.overlay} role="presentation" onClick={onClose}>
        <div
          className={styles.panel}
          role="dialog"
          aria-modal="true"
          aria-labelledby="login-modal-title"
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </LoginModalContext.Provider>
  );
}

// ── Header ──────────────────────────────────────────────────────────────
interface HeaderProps {
  children: ReactNode;
}
function Header({ children }: HeaderProps) {
  const { onClose } = useLoginModalContext();
  return (
    <div className={styles.header}>
      <h2 id="login-modal-title" className={styles.title}>{children}</h2>
      <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Cerrar">
        <CloseIcon />
      </button>
    </div>
  );
}

// ── Body (formulario) ─────────────────────────────────────────────────
interface BodyProps {
  onSuccess: () => void;
}
function Body({ onSuccess }: BodyProps) {
  const { login } = useAuth();
  const { formState, setField, isValid } = useLoginForm();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Se pide de nuevo (queda en false) cada vez que se abre el modal o el
  // widget expira — evita reusar una verificación vieja de otra sesión.
  const [turnstileVerified, setTurnstileVerified] = useState(false);
  // Referencias estables -- si pasáramos closures inline como prop, el
  // useEffect de TurnstileWidget (que depende de onVerified/onError) se
  // re-ejecutaría en cada render del form (cada tecla tipeada), destruyendo
  // y recreando el widget -- y re-disparando la verificación contra el
  // backend una y otra vez sin que el usuario haga nada.
  const handleTurnstileVerified = useCallback(() => setTurnstileVerified(true), []);
  const handleTurnstileError = useCallback(() => setTurnstileVerified(false), []);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>): Promise<void> => {
      e.preventDefault();
      setIsSubmitting(true);

      const success = await login({ username: formState.username, password: formState.password });

      setIsSubmitting(false);

      if (success) {
        setError(null);
        onSuccess();
      } else {
        setError('Usuario o contraseña inválida');
      }
    },
    [login, formState, onSuccess],
  );

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <label className={styles.label} htmlFor="login-username">
        Usuario o correo electrónico
      </label>
      <input
        id="login-username"
        className={styles.input}
        type="text"
        autoComplete="username"
        value={formState.username}
        onChange={(e) => setField('username', e.target.value)}
      />

      <label className={styles.label} htmlFor="login-password">
        Contraseña
      </label>
      <input
        id="login-password"
        className={styles.input}
        type="password"
        autoComplete="current-password"
        value={formState.password}
        onChange={(e) => setField('password', e.target.value)}
      />

      {/* Verificación humana — habilita el botón de "Iniciar sesión" */}
      <TurnstileWidget
        onVerified={handleTurnstileVerified}
        onError={handleTurnstileError}
      />

      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      <button
        type="submit"
        className={styles.submitBtn}
        disabled={!isValid || !turnstileVerified || isSubmitting}
      >
        {isSubmitting ? 'Ingresando…' : 'Iniciar sesión'}
      </button>
    </form>
  );
}

// ── Export del compound component ─────────────────────────────────────
export const LoginModal = { Root, Header, Body };
